<template>
  <div>
    <h2 class="font-bold text-18px pb-0.5">Notes</h2>
    <div v-loading="loading">
      <template v-if="notes.length">
        <div class="bg-white rounded-md border border-grayNeutralLight p-2 mb-0.5 last:mb-0" v-for="(asset, index) in notes" :key="index">
          <div class="flex items-center gap-1 w-full mb-2">
            <div class="flex items-center gap-1 cursor-pointer" @click="toggle(index)">
              <template v-if="asset.notes.length">
                <i :class="isOpen(index) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
                <svg v-if="icon" width="14" height="16">
                  <use :xlink:href="`#${icon}`"></use>
                </svg>
                <svg width="16" height="17">
                  <use :xlink:href="`#doc`"></use>
                </svg>
              </template>
              <h3 class="font-bold text-dark mb-0">{{ asset.name }}</h3>
              <span
                v-if="asset.notes.length"
                class="w-3.5 h-3.5 text-[#909399] bg-grayLightest inline-flex justify-center items-center rounded-full font-bold"
              >
                {{ asset.notes.length }}
              </span>
            </div>
            <ElButton
              v-if="notes.length && canManageNotes"
              type="secondary"
              @click="addNote(asset)"
              class="ml-auto bg-primary/10 text-primary button-custom-icon flex items-center gap-1"
            >
              <i class="el-icon-plus" />
              Add Note
            </ElButton>
          </div>

          <div v-if="asset.notes.length" v-show="isOpen(index)">
            <div class="flex items-center gap-2 py-0.5" v-for="(item, index) in asset.notes" :key="index">
              <div class="p-2 bg-[#F9F9FB] border border-grayNeutralLight rounded w-5 h-5 shrink-0">
                <svg width="16" height="17">
                  <use :xlink:href="`#doc`"></use>
                </svg>
              </div>
              <div class="flex-grow">
                <div class="text-dark mb-1">{{ item.note }}</div>
                <a v-if="item.link" :href="item.link" target="_blank" class="text-primary text-sm">{{ item.link }}</a>
              </div>
              <span class="text-gray mr-2">{{ formatDate(item.created_at) }}</span>
              <!-- Delete -->
              <ElButton v-if="canManageNotes" type="text" class="text-grayNeutralDark p-1" @click="deleteNote(item)">
                <svg width="16" height="16">
                  <use :xlink:href="`#delete-slim`"></use>
                </svg>
              </ElButton>
              <!-- Edit -->
              <ElButton v-if="canManageNotes" @click="editNote(asset, item)" type="text" class="text-grayNeutralDark p-1 ml-0">
                <svg width="16" height="16">
                  <use :xlink:href="`#edit`"></use>
                </svg>
              </ElButton>
            </div>
          </div>

          <!-- No notes found -->
          <div v-else>
            <NoDataCard>
              <template slot="title">No notes</template>
              <template v-if="canManageNotes" slot="info">
                <p>Add notes and links for this asset here.</p>
                <ElButton type="primary" size="small" icon="el-icon-plus" class="mt-2" plain @click="addNote(asset)">Add note</ElButton>
              </template>
              <p v-else slot="info">Notes will be displayed here once available.</p>
            </NoDataCard>
          </div>
        </div>
      </template>
      <!-- No assets found -->
      <NoDataCard v-else>
        <template slot="title">No notes found</template>
        <template slot="info">Notes will be displayed here once available.</template>
      </NoDataCard>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import NoDataCard from '@/components/social-products/social-boost/parts/NoDataCard.vue'
import DeleteNote from '@/components/social-products/social-boost/modal/note/DeleteNote.vue'
import AddEditNote from '@/components/social-products/social-boost/modal/note/AddEditNote.vue'

export default {
  components: {
    NoDataCard,
  },
  props: {
    icon: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      openStates: {},
      loading: false,
    }
  },
  created() {
    this.init()
  },
  computed: {
    ...mapGetters('socialBoostCampaign', ['campaign']),
    flight() {
      return this.campaign?.flights?.find((f) => f.id === Number(this.$route.params.flight_id))
    },
    flightStatus() {
      return this.flight?.status
    },
    notes() {
      return this.$store.getters['socialBoostCampaign/notes'] || []
    },
    canManageNotes() {
      return this.$canAccess(['traffic-manager']) && this.$SBCampaignStatusAccess(['active'], this.flightStatus)
    },
  },
  methods: {
    formatDate(dateString) {
      return new Date(dateString).toLocaleString()
    },
    async init() {
      this.loading = true
      await this.$store.dispatch('socialBoostCampaign/getNotes', { flight_id: this.$route.params.flight_id })
      if (this.notes.length > 0) {
        this.$set(this.openStates, 0, true)
      }
      this.loading = false
    },
    toggle(index) {
      this.$set(this.openStates, index, !this.openStates[index])
    },
    isOpen(index) {
      return this.openStates[index] || false
    },
    deleteNote(note) {
      this.$Modal.init(
        {
          propsData: {
            component: DeleteNote,
            meta: { note },
            size: '480px',
          },
        },
        this
      )
    },
    addNote(asset) {
      this.$Modal.init(
        {
          propsData: {
            component: AddEditNote,
            meta: {
              asset,
              onSaved: () => this.init(),
            },
            size: '480px',
          },
        },
        this
      )
    },
    editNote(asset, note) {
      this.$Modal.init(
        {
          propsData: {
            component: AddEditNote,
            meta: {
              asset,
              note,
              onSaved: () => this.init(),
            },
            size: '480px',
          },
        },
        this
      )
    },
  },
}
</script>
<style lang="scss">
.button-custom-icon {
  span {
    display: flex;
  }
}
</style>
